import { useAuth } from '@teko/auth-react';
import { DiscoveryProduct } from '@teko/discovery';
import { styled } from '@teko/ds-desktop';
import { ProductCarouselWithHeader } from '@teko/teko-ecommerce';
import { WishlistButton } from 'components/WishlistButton';
import {
  useCartV2,
  useHelper,
  useProductDetailState,
  useServices,
  useTenantConfig,
} from 'contexts';
import { isHomepage } from 'helpers';
import { useHandlerUnderEighteenYearOld } from 'hooks/useHandlerUnderEighteenYearOld';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import useSWR from 'swr';

export const SGCFrequentlyBoughtTogether = () => {
  const {
    isMobile,
    contactLink,
    isAllowATCFromListing,
    isDisplayUom,
    showNewProductGift,
  } = useTenantConfig();
  const cartV2 = useCartV2();
  const { discoveryService } = useServices();
  const { product } = useProductDetailState();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const router = useRouter();
  const { loading: authLoading } = useAuth();
  const { productHelper } = useHelper();

  const discoveryCleanUpConfig = {
    showUom: isDisplayUom,
    contactLink,
    showCtaButton: isAllowATCFromListing,
    cartV2,
    showNewProductGift,
    hideHighlightAttribute: isHomepage(router.asPath),
    enableTrackOpenInNewTab:
      router.asPath.includes('/search') || router.asPath.includes('/c/'),
  };

  const transformProduct = useCallback(
    (product: DiscoveryProduct) => {
      return transformProductWhenUserUnder18(
        productHelper.convertNewProductCard(product, {
          ...discoveryCleanUpConfig,
          wishlistButton: {
            children: <WishlistButton sku={product.productInfo?.sku} />,
          },
          bestComboDiscount: product?.prices?.[0]?.bestComboDiscount,
          benefitLabel: product?.productInfo?.benefitLabel,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(cartV2.flattenItems), authLoading],
  );

  const { data: recommendationsProducts = [] } = useSWR(
    [`/products/${product?.sku}/recommendations`],
    async () => {
      const response = await discoveryService.getRecommendedSkus(
        product?.sku || '',
      );

      return response || [];
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  if (!recommendationsProducts?.length) return null;

  const arrowStyle = {
    backgroundColor: 'white',
    borderRadius: 999,
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 5px 15px',
  };

  return (
    <HorizontalProductWrapper isMobile={isMobile}>
      <ProductCarouselWithHeader
        header={{
          title: t('Hay được mua cùng'),
          style: {
            height: `${isMobile ? '2.75rem' : '2.8rem'}`,
            alignItems: 'flex-end',
          },
        }}
        products={recommendationsProducts?.map(transformProduct)}
        productsPerRow={isMobile ? 2.3 : 6.5}
        dividerColor="divider"
        regionName="recommendProducts"
        style={{ minHeight: 'auto' }}
        customStyleLeftArrow={arrowStyle}
        customStyleRightArrow={arrowStyle}
        desktopProps={{ perSwipe: '', bound: true }}
      />
    </HorizontalProductWrapper>
  );
};

const HorizontalProductWrapper = styled.div<{ isMobile: boolean }>(
  ({ isMobile }) => `
  background: #fff;
  margin-top: 8px;
  border-radius: 16px;

  .wrapper-horizontal-slider {
    padding: 0px 8px;
  }

  .title {
  ${
    isMobile
      ? `font-size: 18px;
    line-height: 24px;`
      : `font-size: 20px;
    line-height: 28px;`
  }
    
    font-weight: 700;
  }
`,
);
